import {ReactNode, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {Link, useNavigate} from 'react-router-dom';
import moment from 'moment';
import {ChevronRight, Mail, PhoneMissed, SquareArrowOutUpRight, User, Users} from 'lucide-react';
import {CountBadge, DsAvatar, PhoneHQGlyph, PhoneHQButton, PhoneHQModal, StatusBadge} from 'ch3-ui-lib';
import {FlagImage} from 'react-international-phone';
import {RootState} from '../../store/store';
import {selectUserId} from '../../store/features/Auth/authSlice';
import {Call, useFetchHistoryListQuery} from '../../store/features/calls/callsService';
import {Msisdn, useGetUserMsisdnsQuery} from '../../store/features/Msisdns/msisdnService';
import {selectSelectedPhoneNumber} from '../../store/features/calls/dialerSlice';
import {useMatrixContext} from '../../providers/MatrixProvider';
import {useIsChatEnabledQuery} from '../../services/chatService';
import {matrixService} from '../../services/matrixService';
import {buildQueryParams} from '../../utilis/buildQueryParams';
import {isGroupCall} from '../../utilis/callsUtilis';
import {getFormatedPhoneNumber} from '../../utilis/msisdnUtilis';
import {ClickToCall} from '../../components/ClickToCall/ClickToCall';
import {callerName, isKnownCaller, missedCallersToday, roomName, unreadRooms, type MissedCaller} from './homeData';
import ios from '../../assets/ios-img.svg';
import android from '../../assets/android-img.svg';
import chrome from '../../assets/chrome-img.svg';
import qrAndroid from '../../assets/qr-google-play.svg';
import qrApple from '../../assets/qr-app-store.svg';
import {useCallsPollingInterval} from '../../features/callChanges/useCallsPollingInterval';

const ROWS_PER_CARD = 4;

const lucide = 'shrink-0 !p-0 !text-current';

interface HomeCardProps {
    title: string;
    count?: number;
    countLabel?: string;
    footer?: { label: string; to: string };
    children: ReactNode;
}

const HomeCard = ({title, count, countLabel, footer, children}: HomeCardProps) => (
    <section aria-label={title} className="flex flex-col overflow-hidden rounded-card bg-surface">
        <div className="flex h-[52px] shrink-0 items-center gap-2 px-4">
            <h2 className="m-0 text-body-strong text-ink">{title}</h2>
            {Boolean(count) && <CountBadge count={count!} label={countLabel}/>}
        </div>
        <div className="flex flex-1 flex-col pb-1">{children}</div>
        {footer && (
            <Link
                to={footer.to}
                className="flex h-11 items-center justify-between border-0 border-t border-solid border-line px-4 text-callout font-semibold text-brand-ink hover:bg-surface-muted"
            >
                {footer.label}
                <ChevronRight size={16} aria-hidden="true" className={lucide}/>
            </Link>
        )}
    </section>
);

const HomeRow = ({avatar, title, subline, trailing, onClick}: {
    avatar: ReactNode;
    title: string;
    subline: ReactNode;
    trailing?: ReactNode;
    onClick?: () => void;
}) => {
    const content = (
        <>
            {avatar}
            <div className="min-w-0 flex-1 text-left">
                <div className="truncate text-callout font-semibold text-ink">{title}</div>
                <div className="mt-0.5 flex min-w-0 items-center gap-2 text-footnote text-ink-muted">{subline}</div>
            </div>
            {trailing}
        </>
    );
    const className = 'flex h-[52px] w-full items-center gap-3 px-4';
    return onClick
        ? <button type="button" onClick={onClick} className={`${className} cursor-pointer hover:bg-surface-muted`}>{content}</button>
        : <div className={className}>{content}</div>;
};

const EmptyCard = ({children}: { children: ReactNode }) => (
    <div className="flex flex-1 items-center justify-center px-4 py-6 text-center text-callout text-ink-muted">{children}</div>
);

const LineTile = ({line}: { line: Call['exposedMsisdn'] }) => (
    <span
        role="img"
        aria-label={`Line: ${line.label}`}
        className="flex size-7 shrink-0 items-center justify-center rounded-chip bg-surface text-ink-secondary shadow-[inset_0_0_0_1px_var(--line)]"
    >
        <PhoneHQGlyph name={line.icon || 'call'} size={14}/>
    </span>
);

const MissedCallsCard = ({callers}: { callers: MissedCaller[] }) => {
    const total = callers.reduce((sum, caller) => sum + caller.count, 0);
    return (
        <HomeCard
            title="Missed calls"
            count={total}
            countLabel={`${total} missed`}
            footer={{label: 'Go to calls', to: '/calls'}}
        >
            {callers.length === 0 && <EmptyCard>No missed calls today</EmptyCard>}
            {callers.slice(0, ROWS_PER_CARD).map(({call, count}) => {
                const name = callerName(call, roomId => matrixService.getRoom(roomId)?.name);
                return (
                    <HomeRow
                        key={call.callId}
                        avatar={isGroupCall(call)
                            ? <DsAvatar size={32} icon={<Users size={16} aria-hidden="true" className={lucide}/>}/>
                            : isKnownCaller(call)
                                ? <DsAvatar name={name} size={32}/>
                                : <DsAvatar size={32} icon={<User size={16} aria-hidden="true" className={lucide}/>}/>}
                        title={name}
                        subline={
                            <>
                                <StatusBadge tone="danger" icon={<PhoneMissed size={12} aria-hidden="true" className={lucide}/>}>
                                    {count > 1 ? `Missed ×${count}` : 'Missed'}
                                </StatusBadge>
                                {call.spamCaller && <StatusBadge tone="warning">Likely spam</StatusBadge>}
                                {isKnownCaller(call) && !call.internalCallee && call.clientMsisdn && (
                                    <span className="truncate">{getFormatedPhoneNumber(call.clientMsisdn, false)}</span>
                                )}
                            </>
                        }
                        trailing={
                            <>
                                <span className="shrink-0 text-caption text-danger-ink">{moment(call.startedAt).format('HH:mm')}</span>
                                {call.exposedMsisdn && <LineTile line={call.exposedMsisdn}/>}
                            </>
                        }
                    />
                );
            })}
        </HomeCard>
    );
};

const UnreadChatsCard = () => {
    const navigate = useNavigate();
    const {roomsWithLastMessages} = useMatrixContext();
    const rooms = useMemo(() => unreadRooms(roomsWithLastMessages), [roomsWithLastMessages]);
    const total = rooms.reduce((sum, room) => sum + room.unreadCount, 0);

    const openRoom = (roomId: string) => {
        navigate('/chat');
        matrixService.selectRoom(roomId);
    };

    return (
        <HomeCard
            title="Unread chats"
            count={total}
            countLabel={`${total} unread`}
            footer={{label: 'Go to chat', to: '/chat'}}
        >
            {rooms.length === 0 && <EmptyCard>You're all caught up</EmptyCard>}
            {rooms.slice(0, ROWS_PER_CARD).map((room) => {
                const name = roomName(room);
                return (
                    <HomeRow
                        key={room.roomId}
                        onClick={() => openRoom(room.roomId)}
                        avatar={<DsAvatar name={name} size={32}/>}
                        title={name}
                        subline={<span className="truncate">{room.lastMessage?.content ?? ''}</span>}
                        trailing={<CountBadge count={room.unreadCount} label={`${room.unreadCount} unread`}/>}
                    />
                );
            })}
        </HomeCard>
    );
};

const LinesCard = ({lines, defaultLineId}: { lines: Msisdn[]; defaultLineId?: number }) => (
    <HomeCard title="Your lines" footer={{label: 'Manage lines', to: '/phoneNumbers'}}>
        {lines.length === 0 && <EmptyCard>No lines assigned to you yet</EmptyCard>}
        {lines.slice(0, ROWS_PER_CARD).map((line) => (
            <HomeRow
                key={line.msisdnId}
                avatar={<DsAvatar size={32} palette="blue" icon={<PhoneHQGlyph name={line.icon || 'call'} size={16}/>}/>}
                title={line.label}
                subline={<span className="truncate">{getFormatedPhoneNumber(line.msisdn, false)}</span>}
                trailing={line.msisdnId === defaultLineId && <StatusBadge tone="neutral" className="!h-[22px] !text-caption">Default</StatusBadge>}
            />
        ))}
    </HomeCard>
);

const HelpLink = ({label, children}: { label: string; children: ReactNode }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-footnote text-ink-muted-strong">{label}</span>
        <span className="flex items-center gap-2 text-callout font-semibold text-brand-ink">{children}</span>
    </div>
);

const NeedHelp = () => (
    <section aria-label="Need help" className="flex flex-col gap-3">
        <h2 className="m-0 text-body-strong text-ink">Need help</h2>
        <div className="flex flex-wrap items-center gap-x-10 gap-y-2">
            <HelpLink label="Phone number EN">
                <FlagImage size={16} iso2="us"/>
                <ClickToCall className="text-callout font-semibold text-brand-ink hover:underline" noPrefix msisdn={13054045370} icon={false}/>
            </HelpLink>
            <HelpLink label="Phone number PL">
                <FlagImage size={16} iso2="pl"/>
                <ClickToCall className="text-callout font-semibold text-brand-ink hover:underline" noPrefix msisdn={48884409000} icon={false}/>
            </HelpLink>
            <HelpLink label="Email">
                <a href="mailto:support@phonehq.com" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-brand-ink">
                    support@phonehq.com
                    <Mail size={16} aria-hidden="true" className={lucide}/>
                </a>
            </HelpLink>
            <a
                href="https://support.phonehq.com/en"
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 rounded-[12px] border border-solid border-line bg-surface px-3 py-2 hover:bg-surface-muted"
            >
                <HelpLink label="Knowledge base">
                    <span className="underline">support.phonehq.com</span>
                    <SquareArrowOutUpRight size={16} aria-hidden="true" className={lucide}/>
                </HelpLink>
            </a>
        </div>
    </section>
);

const syncTile = 'flex h-[72px] w-full cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[12px] border border-solid border-line bg-surface text-callout text-ink hover:bg-surface-muted';

const QrTile = ({icon, label, store, qr}: { icon: string; label: string; store: string; qr: string }) => {
    const [open, setOpen] = useState(false);
    return (
        <PhoneHQModal
            open={open}
            onOpenChange={setOpen}
            title={store}
            width={400}
            trigger={
                <button type="button" className={`${syncTile} box-border`}>
                    <img src={icon} alt="" className="size-6"/>
                    <span>{label}</span>
                </button>
            }
            footer={<PhoneHQButton variant="secondary" className="px-5" onClick={() => setOpen(false)}>Close</PhoneHQButton>}
        >
            <p className="m-0 mb-4 text-callout font-normal text-ink-muted">Scan the QR code to download PhoneHQ.</p>
            <div className="flex items-center justify-center rounded-card bg-canvas p-4"><img className="w-32" src={qr} alt={`${store} QR code`}/></div>
        </PhoneHQModal>
    );
};

const SyncPlatforms = () => (
    <section aria-label="Sync across all platforms" className="flex flex-col gap-3">
        <h2 className="m-0 text-body-strong text-ink">Sync across all platforms</h2>
        <div className="grid grid-cols-3 gap-3">
            <QrTile icon={ios} label="Download iOS app" store="App Store" qr={qrApple}/>
            <QrTile icon={android} label="Download Android app" store="Google Play" qr={qrAndroid}/>
            <a
                className={syncTile}
                target="_blank"
                rel="noreferrer"
                href="https://chromewebstore.google.com/detail/phonehq-click-to-call/fkobmebkadldpjlnlligamakjibhngim"
            >
                <img src={chrome} alt="" className="size-6"/>
                <span className="inline-flex items-center gap-1.5">
                    Chrome Extension
                    <SquareArrowOutUpRight size={14} aria-hidden="true" className={lucide}/>
                </span>
            </a>
        </div>
    </section>
);

const HomePage = () => {
    const userId = useSelector(selectUserId);
    const {data: isChatEnabled} = useIsChatEnabledQuery();
    const defaultLine = useSelector((state: RootState) => selectSelectedPhoneNumber(state)) as Msisdn | undefined;

    const todayParams = buildQueryParams({
        limit: 50,
        from: moment().startOf('day').toISOString(),
        to: moment().endOf('day').toISOString(),
        userId,
    });
    const historyPollingInterval = useCallsPollingInterval(15000);
    const {data: history} = useFetchHistoryListQuery(todayParams, {pollingInterval: historyPollingInterval, skip: !userId});
    const missed = useMemo(() => missedCallersToday(history?.calls ?? []), [history]);

    const {data: lines} = useGetUserMsisdnsQuery({userId, phrase: ''}, {skip: !userId});

    return (
        // The greeting ("Hey <name>") is the page header above, not part of this view.
        <div className="flex h-[calc(100vh-var(--header-height))] flex-col bg-canvas">
            <div className="flex min-h-0 flex-1 justify-center overflow-y-auto">
                <div className="flex w-full max-w-[1240px] flex-col gap-6 p-6">
                    <div className="flex flex-col gap-4">
                        <h2 className="m-0 text-title text-ink">Today</h2>
                        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-3">
                            <MissedCallsCard callers={missed}/>
                            {isChatEnabled !== false && <UnreadChatsCard/>}
                            <LinesCard lines={lines?.data ?? []} defaultLineId={defaultLine?.msisdnId}/>
                        </div>
                    </div>
                    <NeedHelp/>
                    <SyncPlatforms/>
                </div>
            </div>
        </div>
    );
};

export default HomePage;
